<template>
  <section>
    <scroll-container
      class="pt-2 mt-n30px"
      :height-offset="isInEditMode ? '55px' : '125px'"
    >
      <b-row v-if="loading">
        <b-col
          sm="12"
          class="text-center"
        >
          <b-skeleton
            class="d-inline-block"
            animation="wave"
            width="40%"
            height="900px"
          />
        </b-col>
      </b-row>
      <b-container
        v-else
        class="container-small"
      >
        <b-card
          class="mb-3"
        >
          <div class="mb-2">
            <b-card-title
              v-if="!isInEditMode"
              class="mb-0"
            >
              {{ $tk('ClientPortal.Form.NewCompanionAd') }}
            </b-card-title>
            <b-card-title
              v-else
              class="mb-0"
            >
              {{ $tk('ClientPortal.Form.EditCompanionAd') }}
            </b-card-title>
            <b-breadcrumb class="m-0 p-0">
              <b-breadcrumb-item :to="`/${accountId}/campaign/edit/${model.campaignModel.id}`">
                {{ model.campaignModel.name }}
              </b-breadcrumb-item>
              <b-breadcrumb-item :to="`/${accountId}/adgroup/edit?adGroupId=${model.adGroupModel.id}&campaignId=${model.campaignModel.id}`">
                {{ model.adGroupModel.name }}
              </b-breadcrumb-item>
              <b-breadcrumb-item
                v-if="!isInEditMode"
                active
              >
                {{ $tk('ClientPortal.Form.NewAd') }}
              </b-breadcrumb-item>
              <b-breadcrumb-item
                v-else
                active
              >
                {{ model.companionAdModel.name }}
              </b-breadcrumb-item>
            </b-breadcrumb>
            <hr class="m-0 mt-1">
          </div>
          <b-form-group
            v-if="!isInEditMode"
            class="mb-2"
            :label="$tk('ClientPortal.Form.CompanionType') + ' *'"
          >
            <v-select
              v-model="model.companionAdModel.type"
              :clearable="false"
              label="text"
              :placeholder="$tk('ClientPortal.Form.SelectCompanionAdType')"
              :options="model.companionAdTypes"
              :reduce="type => type.value"
            />
          </b-form-group>
          <b-form-group
            class="mb-2"
            :label="$tk('ClientPortal.Form.AdName') + ' *'"
            :invalid-feedback="errors['Name']"
          >
            <b-form-input
              id="Name"
              v-model="model.companionAdModel.name"
              type="text"
              :state="errors['Name'] == null ? null : false"
              :placeholder="$tk('ClientPortal.Form.AdNamePlaceholder')"
            />
          </b-form-group>
          <b-form-group
            class="mb-2"
            :invalid-feedback="errors['Url']"
          >
            <legend class="col-form-label pt-0">
              {{ $tk('ClientPortal.Form.Url') }} *
              <GjIcon
                v-b-tooltip.hover="$tk('ClientPortal.Form.URLTooltip')"
                name="Info"
              />
            </legend>
            <b-form-input
              id="Url"
              v-model="model.companionAdModel.videoAd.url"
              type="text"
              :state="errors['Url'] == null ? null : false"
              :placeholder="$tk('ClientPortal.Form.UrlPlaceholder')"
            />
          </b-form-group>
          <b-button
            v-b-toggle.url-builder-video
            class="bg-white mb-2"
            size="sm"
            variant="outline-primary"
          >
            <GjIcon
              class="mr-50"
              name="Hyperlink"
            />
            <span class="when-open">
              {{ $tk('ClientFormat.Form.CloseUrlBuilder') }}
              <GjIcon
                class="mr-50"
                name="ArrowUp"
              />
            </span>
            <span class="when-closed">
              {{ $tk('ClientFormat.Form.OpenUrlBuilder') }}
              <GjIcon
                class="mr-50"
                name="ArrowDown"
              />
            </span>
          </b-button>
          <b-collapse
            id="url-builder-video"
          >
            <url-builder
              v-model="model.companionAdModel.videoAd.url"
              :options="model.companionAdModel.videoAd.utmOptions"
            />
          </b-collapse>
          <b-form-group
            class="mb-2"
          >
            <legend class="col-form-label pt-0">
              {{ $tk('ClientPortal.Form.ImpressionTag') }}
              <GjIcon
                v-b-tooltip.hover="$tk('ClientPortal.Form.ImpressionTagTooltip')"
                name="Info"
              />
            </legend>
            <b-form-textarea
              v-model="model.companionAdModel.videoAd.impressionTag"
              type="text"
              :placeholder="$tk('ClientPortal.Form.ImpressionTagPlaceholder')"
              rows="3"
            />
          </b-form-group>
          <b-form-group
            class="mb-2"
          >
            <legend class="col-form-label pt-0">
              {{ $tk('ClientPortal.Form.SkipTag') }}
              <GjIcon
                v-b-tooltip.hover="$tk('ClientPortal.Form.SkipTagTooltip')"
                name="Info"
              />
            </legend>
            <b-form-textarea
              v-model="model.companionAdModel.videoAd.skipTag"
              type="text"
              :placeholder="$tk('ClientPortal.Form.SkipTagPlaceholder')"
              rows="3"
            />
          </b-form-group>
          <b-form-group
            class="mb-2"
          >
            <legend class="col-form-label pt-0">
              {{ $tk('ClientPortal.Form.ActionTag') }}
              <GjIcon
                v-b-tooltip.hover="$tk('ClientPortal.Form.ActionTagTooltip')"
                name="Info"
              />
            </legend>
            <b-form-textarea
              v-model="model.companionAdModel.videoAd.actionTag"
              type="text"
              :placeholder="$tk('ClientPortal.Form.ActionTagPlaceholder')"
              rows="3"
            />
          </b-form-group>
          <b-row>
            <b-col sm="8">
              <b-card-text class="mb-50">
                {{ $tk('ClientPortal.Form.EnableExternalAds') }}
                <GjIcon
                  v-b-tooltip.hover="$tk('ClientPortal.Form.EnableExternalAdsTooltip')"
                  name="Info"
                />
              </b-card-text>
            </b-col>
            <b-col
              sm="4"
              class="text-right"
            >
              <b-form-checkbox
                v-model="model.companionAdModel.videoAd.external"
                class="d-block"
                switch
                @change="externalSwitched()"
              />
            </b-col>
          </b-row>
          <hr class="mt-50 mb-1">
          <div v-show="!model.companionAdModel.videoAd.external">
            <b-form-group
              class="mb-2"
            >
              <legend class="col-form-label pt-0">
                {{ $tk('ClientPortal.Form.Video') }} {{ '*' }}
                <GjIcon
                  v-b-tooltip.hover="$tk('ClientPortal.Form.VideoTooltip')"
                  name="Info"
                />
              </legend>
              <multiselect
                v-model="selectedVideo"
                :options="model.videos"
                :searchable="true"
                :show-labels="false"
                :placeholder="$tk('ClientPortal.Form.SearchOrSelectVideo')"
                track-by="id"
                label="title"
                @focus.stop=""
                @select="(selectedVideo, id) => setSelectedVideo(selectedVideo)"
              >
                <template
                  slot="option"
                  slot-scope="props"
                >
                  <b-row>
                    <b-col sm="2">
                      <div
                        v-if="props.option.thumbnailUrl != null"
                        class="selected-item-thumbnail d-inline-block"
                        :style="{ backgroundImage: 'url(' + props.option.thumbnailUrl + ')' }"
                      />
                    </b-col>
                    <b-col
                      class=" d-flex"
                      sm="10"
                    >
                      <div class="selected-item-title d-inline-block align-self-center">
                        {{ props.option.title }}
                      </div>
                    </b-col>
                  </b-row>
                </template>
                <template
                  slot="caret"
                  slot-scope="{ toggle }"
                >
                  <div
                    class="multiselect__select"
                    @mousedown.prevent.stop="toggle()"
                  >
                    <GjIcon
                      class="icon-wrapper"
                      name="ArrowDown_fill"
                      size="21"
                    />
                  </div>
                </template>
              </multiselect>
            </b-form-group>
            <text-divider :text="$tk('ClientPortal.Form.Or')" />
            <div class="text-center mt-2">
              <b-button
                variant="outline-primary"
                size="sm"
                @click.prevent="uploadVideo"
              >
                <GjIcon
                  name="Plus"
                  class="mr-50"
                />
                {{ $tk('ClientPortal.Form.UploadAVideo') }}
              </b-button>
            </div>
            <span
              v-if="errors['VideoId']"
              class="field-validation-valid text-danger small"
            >
              {{ errors['VideoId'] }}
            </span>
          </div>
          <div v-show="model.companionAdModel.videoAd.external">
            <b-form-group
              id="External"
              class="mb-2"
              :invalid-feedback="errors['External']"
              :state="errors['External'] == null ? null : false"
            >
              <legend class="col-form-label pt-0">
                {{ $tk('ClientPortal.Form.VastUrl') }}
                <GjIcon
                  v-b-tooltip.hover="$tk('ClientPortal.Form.VastUrlTooltip')"
                  name="Info"
                />
              </legend>
              <b-form-input
                v-model="model.companionAdModel.videoAd.vastUrl"
                type="text"
                :placeholder="$tk('ClientPortal.Form.SearchOrSelectVideo')"
                :state="errors['External'] == null ? null : false"
              />
            </b-form-group>
            <b-form-group
              v-if="platformAccessManager.canSeeYoutubeVideoId() && configuration.clientConfiguration.showYoutubeVideoId"
              class="mb-0"
              :invalid-feedback="errors['External']"
              :state="errors['External'] == null ? null : false"
            >
              <legend class="col-form-label pt-0">
                {{ $tk('ClientPortal.Form.YoutubeVideoId') }}
                <GjIcon
                  v-b-tooltip.hover="$tk('ClientPortal.Form.YoutubeVideoIdTooltip')"
                  name="Info"
                />
              </legend>
              <b-form-input
                v-model="model.companionAdModel.videoAd.youtubeVideoId"
                type="text"
                :placeholder="$tk('ClientPortal.Form.SearchOrSelectVideo')"
                :state="errors['External'] == null ? null : false"
              />
            </b-form-group>
          </div>
          <b-card
            class="mb-1 mt-2 preview-card__border"
            no-body
          >
            <b-card-title
              v-b-toggle.collapse-ad-preview
              class="d-flex justify-content-between p-2 mb-0"
              @click="showPreview(selectedVideo)"
            >
              <div
                class="text-primary"
              >
                <GjIcon name="Show" />
                {{ $tk('ClientPortal.Form.AdCreation.AdPreview') }}
              </div>
              <span class="when-open">
                <GjIcon name="ArrowUp" />
              </span>
              <span class="when-closed">
                <GjIcon name="ArrowDown" />
              </span>
            </b-card-title>
            <b-collapse
              id="collapse-ad-preview"
            >
              <b-card-body class="p-0">
                <hr class="mt-0 mb-0">
                <div
                  v-if="previewVideo"
                  class="card w-100"
                >
                  <div class="card-body bg-white">
                    <div
                      v-if="encoding"
                      class="mb-2 encoding-text"
                    >
                      <GjIcon
                        name="SettingsAlt"
                        size="18"
                      />
                      {{ $tk('ClientPortal.Form.AdCreation.VideoIsBeingEncodedPleaseWait') }}
                    </div>
                    <div v-if="previewVideo.mediaUrl">
                      <div class="video-ad">
                        <video
                          id="video"
                          ref="videoAdPreview"
                          width="100%"
                          controls
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <span
                  v-else
                  class="encoding-text"
                >
                  {{ $tk('ClientPortal.Form.AdCreation.NoVideoHasBeenSelectedYet') }}
                </span>
              </b-card-body>
            </b-collapse>
          </b-card>
        </b-card>
      </b-container>
      <!-- End Of Video -->
      <b-container
        class="container-small"
      >
        <b-card
          v-if="model.companionAdModel.type === 1 || model.companionAdModel.type === 2"
          class="mb-3"
        >
          <div class="mb-2">
            <b-card-title
              class="mb-0"
            >
              {{ $tk('ClientPortal.Form.SquareAd') }}
            </b-card-title>
            <span>{{ $tk('ClientPortal.Form.FillInformationForASquareImageAd') }}</span>
          </div>
          <b-form-group
            class="mb-2"
            :invalid-feedback="errors['DisplayAd.Url']"
          >
            <legend class="col-form-label pt-0">
              <b-form-row>
                <b-col>
                  {{ $tk('ClientPortal.Form.Url') }} *
                  <GjIcon
                    v-b-tooltip.hover="$tk('ClientPortal.Form.URLTooltip')"
                    name="Info"
                  />
                </b-col>
                <b-col class="pt-25 text-right">
                  <b-card-text class="text-muted">
                    {{ model.companionAdModel.displayAd.url ? model.companionAdModel.displayAd.url.length : '0' }}/30
                  </b-card-text>
                </b-col>
              </b-form-row>
            </legend>
            <b-form-input
              id="DisplayAd.Url"
              v-model="model.companionAdModel.displayAd.url"
              type="text"
              maxlength="30"
              :placeholder="$tk('ClientPortal.Form.UrlPlaceholder')"
              :state="errors['DisplayAd.Url'] == null ? null : false"
            />
          </b-form-group>
          <b-button
            v-b-toggle.url-builder-square
            class="bg-white mb-2"
            size="sm"
            variant="outline-primary"
          >
            <GjIcon
              class="mr-50"
              name="Hyperlink"
            />
            <span class="when-open">
              {{ $tk('ClientFormat.Form.CloseUrlBuilder') }}
              <GjIcon
                class="mr-50"
                name="ArrowUp"
              />
            </span>
            <span class="when-closed">
              {{ $tk('ClientFormat.Form.OpenUrlBuilder') }}
              <GjIcon
                class="mr-50"
                name="ArrowDown"
              />
            </span>
          </b-button>
          <b-collapse
            id="url-builder-square"
          >
            <url-builder
              v-model="model.companionAdModel.displayAd.url"
              :options="model.companionAdModel.displayAd.utmOptions"
            />
          </b-collapse>
          <b-form-group
            class="mb-2"
          >
            <legend class="col-form-label pt-0">
              {{ $tk('ClientPortal.Form.ImpressionTag') }}
              <GjIcon
                v-b-tooltip.hover="$tk('ClientPortal.Form.ImpressionTagTooltip')"
                name="Info"
              />
            </legend>
            <b-form-textarea
              v-model="model.companionAdModel.displayAd.impressionTag"
              type="text"
              :placeholder="$tk('ClientPortal.Form.ImpressionTagPlaceholder')"
              rows="3"
            />
          </b-form-group>
          <b-form-group
            class="mb-0"
            :label="$tk('Admin.DisplayAds.Image') + '*'"
          >
            <vue-dropzone
              id="dropzone"
              ref="companionDropzone"
              class="upload-img"
              :use-custom-slot="true"
              :options="dropzoneOptions"
              @vdropzone-success="uploadFile"
              @vdropzone-processing="dropzoneChangeUrl('Square')"
              @vdropzone-sending="sendFile"
              @vdropzone-thumbnail="checkFileThumbnail"
              @vdropzone-complete="removeFile"
            >
              <div
                v-if="!model.companionAdModel.displayAd.fileName"
                class="dz-default dz-message dz-message-center mx-3"
              >
                <span class="d-block mb-3 line-height-1 upload-image-dropzone">
                  {{ $tk('ClientPortal.Form.UploadImageInFormat300x250') }}
                </span>
                <div>
                  <b-button
                    variant="primary"
                    size="sm"
                    class="mr-50 mb-50 mb-sm-0"
                  >
                    <GjIcon
                      class="mr-50"
                      name="Hyperlink"
                    />
                    {{ $tk('ClientPortal.Form.UploadFiles') }}
                  </b-button>
                  <b-button
                    variant="outline-primary"
                    size="sm"
                    @click="showGallery($event, 'Square')"
                  >
                    <GjIcon
                      class="mr-50"
                      name="Image"
                    />
                    {{ $tk('ClientPortal.Form.ChooseFromCreatives') }}
                  </b-button>
                </div>
              </div>
              <div
                v-else
                class="image-dropzone"
              >
                <b-button
                  variant="link-secondary"
                  class="icon-dropzone"
                  @click="removeImage('Square')"
                >
                  <GjIcon
                    name="Close_fill"
                  />
                </b-button>
                <GjIcon
                  name="Image"
                  size="45"
                />
                <span class="mt-1">{{ model.companionAdModel.displayAd.fileName }}</span>
                <span class="d-flex justify-content-center mt-25 mb-2">
                  <b-button
                    variant="link"
                    class="btn-underline"
                    @click="sendFile"
                  >
                    {{ $tk('ClientPortal.CompanionAd.UploadFiles') }}
                  </b-button>
                  <span
                    class="or-text"
                  > {{ $tk('ClientPortal.Form.Or') }}</span>
                  <b-button
                    variant="link"
                    class="btn-underline"
                    @click="showGallery($event, 'Square')"
                  >
                    {{ $tk('ClientPortal.Form.ChooseFromCreatives') }}
                  </b-button>
                </span>
              </div>
            </vue-dropzone>
            <span
              v-if="uploadMessage"
              :class="(uploadMessage.results.length) !== 0 ? 'text-success' : 'text-danger'"
              class="field-validation-valid small"
            >
              {{ uploadMessage.message }}
            </span>
          </b-form-group>
          <b-card
            class="mb-1 mt-2 preview-card__border"
            no-body
          >
            <b-card-title
              v-b-toggle.collapse-square-ad-preview
              class="d-flex justify-content-between p-2 mb-0"
            >
              <div
                class="text-primary"
              >
                <GjIcon name="Show" />
                {{ $tk('ClientPortal.Form.AdCreation.AdPreview') }}
              </div>
              <span class="when-open">
                <GjIcon name="ArrowUp" />
              </span>
              <span class="when-closed">
                <GjIcon name="ArrowDown" />
              </span>
            </b-card-title>
            <b-collapse
              id="collapse-square-ad-preview"
            >
              <PreviewImageAd
                :preview-ad="model.companionAdModel.displayAd.show"
                :url="model.companionAdModel.displayAd.mediaUrl"
              />
            </b-collapse>
          </b-card>
        </b-card>
      </b-container>
      <!-- End Of Square -->
      <b-container
        class="container-small"
      >
        <b-card
          v-if="model.companionAdModel.type === 2 || model.companionAdModel.type === 3"
          class="mb-3"
        >
          <div class="mb-2">
            <b-card-title
              class="mb-0"
            >
              {{ $tk('ClientPortal.Form.UnderVideoAd') }}
            </b-card-title>
            <span>{{ $tk('ClientPortal.Form.FillInformationForAUnderVideoAd') }}</span>
          </div>
          <template v-for="(input, index) in model.companionAdModel.nativeAd.customProperties">
            <b-form-group
              v-if="input.inputType == 1"
              :id="input.name"
              :key="index"
              class="mb-2"
              :invalid-feedback="errors[input.name]"
              :state="errors[input.name] == null ? null : false"
            >
              <legend class="col-form-label pt-0">
                <b-form-row>
                  <b-col>
                    {{ input.displayName }} <span v-if="input.required == true">*</span>
                  </b-col>
                  <b-col class="pt-25 text-right">
                    <b-card-text class="text-muted">
                      {{ input.value.length }}/{{ input.maxLength }}
                    </b-card-text>
                  </b-col>
                </b-form-row>
              </legend>
              <b-form-input
                :id="input.name"
                v-model="input.value"
                :placeholder="$tk('ClientPortal.Form.Enter') + ' ' + input.displayName"
                type="text"
                :maxlength="input.maxLength"
                :state="errors[input.name] == null ? null : false"
              />
            </b-form-group>
          </template>
          <b-form-group
            class="mb-2"
            :invalid-feedback="errors['NativeAd.Url']"
          >
            <legend class="col-form-label pt-0">
              <b-form-row>
                <b-col>
                  {{ $tk('ClientPortal.Form.Url') }} *
                  <GjIcon
                    v-b-tooltip.hover="$tk('ClientPortal.Form.URLTooltip')"
                    name="Info"
                  />
                </b-col>
                <b-col class="pt-25 text-right">
                  <b-card-text class="text-muted">
                    {{ model.companionAdModel.nativeAd.url ? model.companionAdModel.nativeAd.url.length : '0' }}/30
                  </b-card-text>
                </b-col>
              </b-form-row>
            </legend>
            <b-form-input
              id="NativeAd.Url"
              v-model="model.companionAdModel.nativeAd.url"
              type="text"
              maxlength="30"
              :placeholder="$tk('ClientPortal.Form.UrlPlaceholder')"
              :state="errors['NativeAd.Url'] == null ? null : false"
            />
          </b-form-group>
          <b-button
            v-b-toggle.url-builder-under-video
            class="bg-white mb-2"
            size="sm"
            variant="outline-primary"
          >
            <GjIcon
              class="mr-50"
              name="Hyperlink"
            />
            <span class="when-open">
              {{ $tk('ClientFormat.Form.CloseUrlBuilder') }}
              <GjIcon
                class="mr-50"
                name="ArrowUp"
              />
            </span>
            <span class="when-closed">
              {{ $tk('ClientFormat.Form.OpenUrlBuilder') }}
              <GjIcon
                class="mr-50"
                name="ArrowDown"
              />
            </span>
          </b-button>
          <b-collapse
            id="url-builder-under-video"
          >
            <url-builder
              v-model="model.companionAdModel.nativeAd.url"
              :options="model.companionAdModel.nativeAd.utmOptions"
            />
          </b-collapse>
          <b-form-group
            class="mb-2"
          >
            <legend class="col-form-label pt-0">
              {{ $tk('ClientPortal.Form.ImpressionTag') }}
              <GjIcon
                v-b-tooltip.hover="$tk('ClientPortal.Form.ImpressionTagTooltip')"
                name="Info"
              />
            </legend>
            <b-form-textarea
              v-model="model.companionAdModel.nativeAd.impressionTag"
              type="text"
              :placeholder="$tk('ClientPortal.Form.ImpressionTagPlaceholder')"
              rows="3"
            />
          </b-form-group>
          <b-form-group
            class="mb-0"
            :label="$tk('Admin.DisplayAds.Image') + '*'"
          >
            <vue-dropzone
              id="dropzone"
              ref="companionUnderVideoDropzone"
              class="upload-img"
              :use-custom-slot="true"
              :options="dropzoneOptions"
              @vdropzone-success="uploadUnderVideoAdFile"
              @vdropzone-processing="dropzoneChangeUrl('UnderVideo')"
              @vdropzone-sending="sendFile"
              @vdropzone-thumbnail="checkFileThumbnail"
              @vdropzone-complete="removeFile"
            >
              <div
                v-if="!showImage"
                class="dz-default dz-message dz-message-center mx-3"
              >
                <span class="d-block mb-3 line-height-1 upload-image-dropzone">
                  {{ $tk('ClientPortal.Form.UploadImage') }}
                </span>
                <div>
                  <b-button
                    variant="primary"
                    size="sm"
                    class="mr-50 mb-50 mb-sm-0"
                  >
                    <GjIcon
                      class="mr-50"
                      name="Hyperlink"
                    />
                    {{ $tk('ClientPortal.Form.UploadFiles') }}
                  </b-button>
                </div>
              </div>
              <div
                v-else
                class="image-dropzone"
              >
                <b-button
                  variant="link-secondary"
                  class="icon-dropzone"
                  @click="removeImage('UnderVideo')"
                >
                  <GjIcon
                    name="Close_fill"
                  />
                </b-button>
                <GjIcon
                  name="Image"
                  size="45"
                />
                <span class="mt-1">{{ model.companionAdModel.nativeAd.customProperties.find(x => x.id == 6).value }}</span>
                <span class="d-flex justify-content-center mt-25 mb-2">
                  <b-button
                    variant="link"
                    class="btn-underline"
                    @click="sendFile"
                  >
                    {{ $tk('ClientPortal.CompanionAd.UploadFiles') }}
                  </b-button>
                </span>
              </div>
            </vue-dropzone>
            <span
              v-if="uploadUnderVideoMessage"
              :class="(uploadUnderVideoMessage.results.length) !== 0 ? 'text-success' : 'text-danger'"
              class="field-validation-valid small"
            >
              {{ uploadUnderVideoMessage.message }}
            </span>
          </b-form-group>
          <section class="mt-2">
            <b-row
              v-if="model.companionAdModel.nativeAd.customProperties.filter(x => x.inputType == 3).length > 0"
              class="mb-2"
            >
              <b-col sm="8">
                <b-card-text class="mb-50">
                  {{ $tk('ClientPortal.Form.CustomizeColors') }}
                </b-card-text>
              </b-col>
              <b-col
                sm="4"
                class="text-right"
              >
                <input
                  v-model="customizeColors"
                  type="hidden"
                  name="customizeColors"
                >
                <b-form-checkbox
                  v-model="customizeColors"
                  class="d-block"
                  checked="false"
                  name="check-button"
                  switch
                />
              </b-col>
            </b-row>
            <b-row
              v-if="customizeColors"
              class="mt-2 mb-n2"
            >
              <template
                v-for="(input, inputIndex) in model.companionAdModel.nativeAd.customProperties"
              >
                <b-col
                  v-if="input.inputType == 3"
                  :key="inputIndex"
                  sm="6"
                >
                  <b-form-group
                    :label="input.displayName"
                  >
                    <b-input-group>
                      <b-input-group-prepend>
                        <v-swatches
                          :id="input.name"
                          v-model="input.value"
                          show-fallback
                          :value="input.value"
                          show-border
                          popover-to="right"
                        />
                      </b-input-group-prepend>
                      <v-swatches
                        :id="input.name"
                        v-model="input.value"
                        show-fallback
                        :value="input.value"
                        show-border
                        popover-to="right"
                      >
                        <input
                          slot="trigger"
                          :value="input.value"
                          class="form__input__element"
                          readonly
                        >
                      </v-swatches>
                    </b-input-group>
                  </b-form-group>
                </b-col>
              </template>
            </b-row>
          </section>
          <b-card
            class="mb-1 mt-2 preview-card__border"
            no-body
            @click="showImagePreview()"
          >
            <b-card-title
              v-b-toggle.collapse-under-video-ad-preview
              class="d-flex justify-content-between p-2 mb-0"
            >
              <div
                class="text-primary"
              >
                <GjIcon name="Show" />
                {{ $tk('ClientPortal.Form.AdCreation.AdPreview') }}
              </div>
              <span class="when-open">
                <GjIcon name="ArrowUp" />
              </span>
              <span class="when-closed">
                <GjIcon name="ArrowDown" />
              </span>
            </b-card-title>
            <b-collapse
              id="collapse-under-video-ad-preview"
            >
              <template>
                <b-card-body class="pt-0">
                  <hr class="mt-0">
                  <b-tabs
                    show="preview"
                    fill
                  >
                    <b-tab
                      active
                    >
                      <template #title>
                        <GjIcon
                          class="mr-50"
                          name="Desktop"
                        />
                        {{ $tk('ClientPortal.Form.AdPreview.Desktop') }}
                      </template>
                      <div
                        style="position:relative;"
                      >
                        <a
                          :href="model.companionAdModel.nativeAd.url"
                          target="_blank"
                        >
                          <div
                            class="text-center"
                          >
                            <iframe
                              id="preview-frame"
                              ref="previewFrameDesktop"
                              class="desktop"
                              style="max-width:600px; border:0px; pointer-events:none; width: 100%;"
                            />
                          </div>
                        </a>
                      </div>
                    </b-tab>
                    <b-tab>
                      <template #title>
                        <GjIcon
                          class="mr-50"
                          name="Mobile"
                        />
                        {{ $tk('ClientPortal.Form.AdPreview.Mobile') }}
                      </template>
                      <div
                        class="d-flex justify-content-center"
                      >
                        <a
                          :href="model.companionAdModel.nativeAd.url"
                          target="_blank"
                        >
                          <div
                            class="text-center"
                          >
                            <iframe
                              id="preview-frame-mobile"
                              ref="previewFrameMobile"
                              style="width:100%; border:0px; pointer-events:none; height:100%"
                            />
                          </div>
                        </a>
                      </div>
                    </b-tab>
                  </b-tabs>
                </b-card-body>
              </template>
            </b-collapse>
          </b-card>
        </b-card>
      </b-container>
    </scroll-container>
    <!-- End Of UnderVideo -->
    <b-modal
      id="gallery-modal"
      ref="galleryModal"
      body-class="modal-body--no-padding creatives-modal"
      hide-footer
      :title="$tk('ClientPortal.Form.Gallery')"
      size="xl"
      centered
      cancel-variant="outline-secondary"
      @hidden="hideGallery"
    >
      <loader
        v-if="loadingGallery"
        :size="24"
        :show="true"
        class="gallery_loader"
      />
      <div v-else>
        <div class="d-block mx-1 mb-1">
          <b-form-input
            v-model="creativeSearchTerm"
            type="text"
            :placeholder="$tk('ClientPortal.Form.SearchCreative')"
            @input="searchCreative"
          />
        </div>
        <hr class="mb-0">
        <scroll-container
          class="pt-1 px-1"
          height-offset="260px"
        >
          <b-row class="mx-n50">
            <b-col
              v-for="(creativeItem, index) in creatives"
              :key="index"
              xl="2"
              md="3"
              sm="6"
              cols="12"
              class="mb-1 px-50"
            >
              <b-card
                class="mb-0 h-100 border"
                :class="{'border-primary' : creativeItem.selected}"
                @click.prevent="selectCreative(creativeItem)"
              >
                <div class="text-right">
                  <b-form-radio
                    v-model="creativeItem.selected"
                    value="true"
                    name="creative"
                  />
                </div>
                <b-card
                  class="mb-2"
                  body-class="p-0"
                >
                  <div
                    class="products-img text-center mx-auto"
                    :style="{ backgroundImage: 'url(' + creativeItem.url + ')' }"
                  />
                </b-card>
                <b-card-title class="mb-0">
                  {{ creativeItem.name }}
                </b-card-title>
              </b-card>
            </b-col>
            <span
              v-if="creatives.length === 0"
              class="gallery-text"
            >
              <div>
                <GjIcon
                  size="64px"
                  name="Grid"
                />
              </div>
              <div>
                <span v-if="creativeSearchTerm.length > 0"> {{ $tk('ClientPortal.CompanionAds.NoCreativesAreFoundWithThatName') }}</span>
                <span v-else> {{ $tk('ClientPortal.CompanionAds.NoCreativesFound') }}</span>
              </div>
            </span>
          </b-row>
          <InfiniteLoading
            v-if="creatives.length > 0"
            @infinite="loadCreatives($event)"
          >
            <div slot="no-results" />
            <div slot="no-more" />
          </InfiniteLoading>
        </scroll-container>
        <div class="p-1 border-top text-right">
          <b-button
            class=""
            variant="outline-secondary"
            size="sm"
            @click.prevent="hideGallery"
          >
            {{ $tk('ClientPortal.Form.Actions.Cancel') }}
          </b-button>
          <b-button
            class="ml-50"
            variant="primary"
            size="sm"
            @click.prevent="addCreativeItems()"
          >
            {{ $tk('ClientPortal.Form.Add') }}
          </b-button>
        </div>
      </div>
    </b-modal>
    <div class="border-top text-right py-1">
      <div class="container-small mx-auto">
        <b-button
          v-if="isInEditMode"
          class="bg-white"
          variant="outline-dark"
          size="sm"
          :disabled="saving"
          @click="closeEditModal"
        >
          {{ $tk('ClientPortal.Form.Actions.Cancel') }}
        </b-button>
        <b-button
          v-else
          :to="`/${$route.params.accountId}`"
          class="bg-white"
          variant="outline-dark"
          size="sm"
          :disabled="saving"
        >
          {{ $tk('ClientPortal.Form.Actions.Cancel') }}
        </b-button>
        <b-button
          class="bg-white ml-50"
          variant="primary"
          size="sm"
          :disabled="saving || !isFormCompleted"
          @click="saveCompanionAd()"
        >
          {{ isInEditMode ? $tk('ClientPortal.Form.Actions.Save') : $tk('ClientPortal.SideMenu.CreateAd') }}
        </b-button>
      </div>
    </div>
    <b-modal
      ref="uploadVideoModal"
      variant="primary"
      :no-close-on-esc="isUploading && barVariant !== 'danger'"
      :no-close-on-backdrop="isUploading && barVariant !== 'danger'"
      :hide-header-close="isUploading && barVariant !== 'danger'"
      centered
      @hidden="clearErrorMsg"
    >
      <template v-slot:modal-title>
        <h5 class="modal-title">
          <span>
            {{ $tk('ClientPortal.Form.UploadVideo') }}
          </span>
        </h5>
      </template>
      <b-row>
        <b-col sm="12">
          <input
            id="video-file-name"
            v-model="videoEdit.fileName"
            type="text"
            :placeholder="$tk('ClientPortal.Form.VideoFileNamePlaceHolder')"
            class="form-control form-control-sm mb-2"
          >
          <input
            id="video-file-id"
            v-model="videoEdit.videoId"
            type="hidden"
            class="form-control mb-2"
          >
          <input
            id="upload-account-id"
            type="hidden"
            :value="accountId"
          >
          <vue-dropzone
            id="dropzone"
            ref="videoDropzone"
            :options="dropzoneOptionsVideo"
            :use-custom-slot="true"
            style="width:100%; text-align: center;"
            @vdropzone-file-added="passFile"
          >
            <div class="dropzone-custom-content">
              <b-button
                variant="outline-primary"
                size="sm"
                @click.stop="openVideoUpload"
              >
                <GjIcon
                  class="mr-50"
                  name="Upload"
                />
                {{ $t('ClientPortal.Form.UploadVideo') }}
              </b-button>
              <div style="pointer-events:none;">
                {{ $tk('ClientPortal.Form.Max8MBFileAllowed') }}
              </div>
            </div>
          </vue-dropzone>
          <input
            id="videoFile"
            ref="openUpload"
            hidden
            accept="video/*"
            type="file"
            name="video"
            :disabled="isUploading && barVariant !== 'danger'"
            @input="onFileSelected"
          >
          <div
            v-if="isUploading"
            class="d-flex flex-column w-100 col-12 p-0 pt-1"
          >
            <b-col
              md="12"
              class=" px-0"
            >
              <uploader-bar
                v-model="fileProgress"
                :variant="barVariant"
                :message="uploaderBarMessage"
              />
            </b-col>
          </div>

        </b-col>
      </b-row>
      <b-row>
        <b-col sm="12">
          <div
            id="progress-bar-holder"
            ref="progressBarHolder"
          />
        </b-col>
      </b-row>
      <template v-slot:modal-footer>
        <b-button
          variant="primary"
          size="sm"
          :disabled="!videoEdit.videoId || barVariant === 'danger'"
          @click="updateVideoName(videoEdit)"
        >
          {{ $tk('ClientPortal.Form.Actions.Save') }}
        </b-button>
      </template>
    </b-modal>
  </section>
</template>

<script>
import GjIcon from '@gjirafatech/gjirafa-icons/Icon.vue'
import Hls from 'hls.js'
import notification from '@/mixins/toastification'
import sweetAlert from '@/mixins/sweetAlert'
import translate from '@/mixins/translate'
import Multiselect from 'vue-multiselect'
import ScrollContainer from '@/components/ScrollContainer.vue'
import UrlBuilder from '@/components/UrlBuilder.vue'
import TextDivider from '@/components/TextDivider.vue'
import { PlatformAccessManager } from '@/utils/accessManager/accessManager'
import axiosIns from '@/libs/axios'
import axios from 'axios'
import UploaderBar from '@/components/UploaderBar.vue'
import AdsService from '@/api/client/ads.service'
import VideoService from '@/api/client/video.service'
import GalleryService from '@/api/client/gallery.service'
import {
  onMounted, ref, reactive,
  computed, inject,
} from '@vue/composition-api'
import store from '@/store'
import PreviewImageAd from '@/components/PreviewImageAd.vue'
import getAPIDomain from '@/utils/apiDomain/apiDomain'
import vue2Dropzone from 'vue2-dropzone'
import 'vue2-dropzone/dist/vue2Dropzone.min.css'
import VSwatches from 'vue-swatches'
import 'vue-swatches/dist/vue-swatches.css'
import scrollToError from '@/mixins/scrollToError'
import vSelect from 'vue-select'
import loader from '@/components/Loader.vue'
import InfiniteLoading from 'vue-infinite-loading'

const ADSUBTYPE_DISPLAY_IMAGE = 8
const VIDEO_FORMAT_ID = 53
const NATIVE_FORMAT_ID = 12
const IMAGE_FORMAT_ID = 1

export default {
  inject: ['closeEditModal'],
  components: {
    GjIcon,
    Multiselect,
    ScrollContainer,
    UrlBuilder,
    TextDivider,
    UploaderBar,
    PreviewImageAd,
    vueDropzone: vue2Dropzone,
    VSwatches,
    InfiniteLoading,
    loader,
    vSelect,
  },
  mixins: [notification, sweetAlert],
  props: {
    // eslint-disable-next-line vue/require-default-prop
    adId: Number,
    // eslint-disable-next-line vue/require-default-prop
    adGroupId: Number,
    // eslint-disable-next-line vue/require-default-prop
    catalogSetId: {
      type: String,
    },
    saving: {
      type: Boolean,
    },
  },
  /* eslint-disable no-use-before-define */
  setup(props, context) {
    const loading = ref(false)
    const { accountId } = context.root.$route.params
    // Data
    const videoEdit = ref({})
    const selectedVideo = ref({})
    const preview = ref(false)
    const previewVideo = ref(null)
    const errors = ref({})
    const value = reactive({})
    const platformAccessManager = PlatformAccessManager
    const isUploading = ref(false)
    const isFileUploading = ref(false)
    const fileProgress = ref(0)
    const barVariant = ref('primary')
    const creativeSearchTerm = ref('')
    const creatives = ref([])
    const creativePage = ref(0)
    const uploadMessage = ref('')
    const savingImage = ref(false)
    const uploadUnderVideoMessage = ref('')
    const isSquare = ref(false)
    const showImage = ref(false)
    const dropzoneOptions = {
      uploadMultiple: false,
      maxFiles: 1,
      parallelUploads: 1,
      acceptedFiles: '.jpg,.png,.jpeg',
      url: 'url',
      thumbnailWidth: null,
      timeout: 60000,
      thumbnailHeight: null,
      addRemoveLinks: true,
      headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` },
    }
    const customizeColors = ref(false)
    const uploaderBarMessage = ref('null')
    const dropzoneOptionsVideo = {
      uploadMultiple: false,
      maxFiles: 1,
      acceptedFiles: '.mp4,.flv',
      url: 'url',
      thumbnailWidth: null,
      timeout: 0,
      thumbnailHeight: null,
      addRemoveLinks: true,
      headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` },
    }
    const encoding = ref(false)

    // Injects
    const closeEditModal = inject('closeEditModal')
    // Computed values
    const configuration = computed(() => store.getters.configuration)
    const model = computed(() => store.getters.getCompanionAdObject)
    const isFormCompleted = computed(() => {
      if (model.value.companionAdModel.type === 1) {
        return model.value.companionAdModel.displayAd.formatId && model.value.companionAdModel.displayAd.mediaUrl && model.value.companionAdModel.name
      }
      if (model.value.companionAdModel.type === 2) {
        return model.value.companionAdModel.displayAd.formatId && model.value.companionAdModel.displayAd.mediaUrl && model.value.companionAdModel.nativeAd.formatId && model.value.companionAdModel.nativeAd.url && model.value.companionAdModel.name
      }
      if (model.value.companionAdModel.type === 3) {
        return model.value.companionAdModel.nativeAd.formatId && model.value.companionAdModel.nativeAd.url && model.value.companionAdModel.name
      }
      return ''
    })
    const isInEditMode = computed(() => props.adId > 0)
    // Life Cycles
    onMounted(async () => {
      loading.value = true
      try {
        await store.dispatch('getCompanionAd', {
          adId: props.adId,
          adGroupId: props.adGroupId,
          catalogSetId: props.catalogSetId,
          accountId,
        })
      } catch (error) {
        notification.methods.error(translate.methods.$tk('ClientPortal.Form.FailedToLoadData'))
      } finally {
        if (isInEditMode.value) {
          if (model.value.companionAdModel.videoAd.videoId > 0) {
            const video = model.value.videos.find(x => x.id === model.value.companionAdModel.videoAd.videoId)
            selectedVideo.value = video
          }
          if (model.value.companionAdModel.displayAd.mediaUrl) {
            model.value.companionAdModel.displayAd.show = true
          }
          if (model.value.companionAdModel.nativeAd.url) {
            showImage.value = true
          }
        } else { // This is when creating ad and user opens url builder
          model.value.companionAdModel.videoAd.url = ''
          model.value.companionAdModel.displayAd.url = ''
          model.value.companionAdModel.nativeAd.url = ''
        }
        loading.value = false
      }
    })
    // Functions
    const externalSwitched = () => {
      model.value.companionAdModel.videoAd.vastUrl = ''
      model.value.companionAdModel.videoAd.videoId = 0
    }

    const setSaving = newValue => {
      context.emit('onUpdate', newValue)
    }

    const saveCompanionAd = async () => {
      setSaving(true)
      const { entity } = context.parent.$parent.$parent
      try {
        const response = await AdsService.saveCompanionAd(model.value, accountId)
        if (response.saved) {
          if (isInEditMode.value) {
            entity.name = response.model.name
            notification.methods.success(response.message)
            closeEditModal()
          } else {
            sweetAlert.methods.sweetAlertAfterAdCreation(accountId)
          }
        } else {
          errors.value = response.model.errors
          notification.methods.error(response.model.errors['VideoAd.Name'])
          context.root.$nextTick(() => {
            scrollToError(errors.value)
          })
        }
      } catch (error) {
        notification.methods.error(error.message)
      }
      setSaving(false)
    }
    const setSelectedVideo = selectedVideoR => {
      selectedVideo.value = selectedVideoR
      model.value.companionAdModel.videoAd.videoId = selectedVideoR.id
      model.value.companionAdModel.videoAd.title = selectedVideoR.title
      model.value.companionAdModel.videoAd.formatId = VIDEO_FORMAT_ID
      model.value.companionAdModel.videoAd.video.title = selectedVideoR.title
      model.value.companionAdModel.videoAd.video.mediaUrl = selectedVideoR.mediaUrl
      model.value.companionAdModel.videoAd.video.message = selectedVideoR.message
      model.value.companionAdModel.videoAd.video.thumbnailUrl = selectedVideoR.thumbnailUrl
    }
    const uploadVideo = () => {
      context.refs.uploadVideoModal.show()
    }
    const updateVideoName = async videoEditR => {
      model.value.companionAdModel.videoAd.video.title = videoEditR.fileName
      try {
        const response = await VideoService.updateVideo(videoEditR.videoId, videoEditR.fileName, accountId)
        if (response.success) {
          model.value.companionAdModel.videoAd.formatId = VIDEO_FORMAT_ID
          context.refs.uploadVideoModal.hide()
          const exists = model.value.videos.filter(x => x.id === videoEditR.videoId)
          const data = {
            id: videoEditR.videoId,
            title: videoEditR.fileName,
            thumbnailUrl: null,
          }
          if (exists.length === 0) {
            model.value.videos.push(data)
            videoEdit.value.id = 0
            videoEdit.value.fileName = ''
          }
          selectedVideo.value = data
          notification.methods.success(response.message)
        } else {
          notification.methods.error(response.message)
        }
      } catch (error) {
        notification.methods.error(error.message)
      }
    }
    const showPreview = async selectedVideoInner => {
      if (preview.value) {
        context.refs.videoAdPreview.pause()
        preview.value = false
      } else {
        encoding.value ? previewVideo.value = {} : previewVideo.value = null
        if (selectedVideoInner != null && selectedVideoInner.id > 0) {
          try {
            const response = await VideoService.getVideo(selectedVideoInner.id, accountId)
            previewVideo.value = response
            if (previewVideo.value.mediaUrl != null) {
              preview.value = true
              context.root.$nextTick(() => {
                loadVideo(previewVideo.value.mediaUrl)
                encoding.value = false
              })
            } else if (!encoding.value) {
              encoding.value = true
              notification.methods.error(previewVideo.value.message)
            }
          } catch (error) {
            notification.methods.error(error.message)
          }
        }
      }
    }
    const clearErrorMsg = () => {
      if (barVariant.value === 'danger' && isUploading.value) {
        isUploading.value = false
        barVariant.value = 'primary'
      }
    }
    const checkFileType = type => type.startsWith('video')
    const passFile = file => {
      context.refs.videoDropzone.removeFile(file)
      /* eslint-disable-next-line no-use-before-define */
      onFileSelected(file, true)
    }
    const onFileSelected = async (e, dragAndDrop = false) => {
      /* eslint-disable no-else-return, no-useless-return */
      if (isFileUploading.value) {
        return
      } else {
        isFileUploading.value = true
        isUploading.value = true
        fileProgress.value = 0
        barVariant.value = 'primary'
        let video
        if (dragAndDrop) {
          video = e
          if (!checkFileType(e.type)) {
            isFileUploading.value = false
            barVariant.value = 'danger'
            uploaderBarMessage.value = translate.methods.$tk('ClientPortal.Ads.VideoAd.UploadFailedWrongFileExtension')
            return
          }
        } else { /* eslint-disable-next-line */
          video = e.target.files[0]
          if (!checkFileType(e.target.files[0].type)) {
            isFileUploading.value = false
            barVariant.value = 'danger'
            uploaderBarMessage.value = translate.methods.$tk('ClientPortal.Ads.VideoAd.UploadFailedWrongFileExtension')
            return
          }
        }
        const params = {
          fileName: video.name,
          mimeType: video.type,
        }
        const headers = {
          'Access-Control-Allow-Origin': '*',
        }
        await axiosIns.post(`upload/presignedurl?accountId=${accountId}`, params, {
          headers,
        }).then(response => {
          const signedUrl = response.data.result.url
          const options = {
            headers: {
              'Access-Control-Allow-Origin': '*',
              'Content-Type': 'application/octet-stream',
              'Passed-Req-Key': response.data.result.requestKey,
              'x-amz-acl': 'public-read',

            },
            onUploadProgress: progressEvent => {
              const { total } = progressEvent
              const totalLength = progressEvent.lengthComputable ? total : null
              if (totalLength !== null) {
                fileProgress.value = Math.round((progressEvent.loaded * 100) / total)
              }
            },
          }
          return axios.put(signedUrl, video, options)
            .then(async result => {
              const encodeHeader = {
                downloadUrl: result.config.headers['Passed-Req-Key'],
              }
              await axiosIns.post('upload/presignedurl/encode', encodeHeader)
                .then(res => {
                  const selectedVideoInner = {
                    id: res.data.result.id,
                    title: res.data.result.name,
                  }
                  videoEdit.value.fileName = res.data.result.name
                  videoEdit.value.videoId = res.data.result.id
                  selectedVideo.value = selectedVideoInner
                  setSelectedVideo(selectedVideoInner)
                  isUploading.value = false
                  fileProgress.value = 0
                })
            })
            .catch(err => {
              if (err) {
                isUploading.value = true
                fileProgress.value = 100
                barVariant.value = 'danger'
              }
            })
        })
          .catch(err => {
            if (err) {
              isUploading.value = true
              fileProgress.value = 100
              barVariant.value = 'danger'
            }
          })
      }
      isFileUploading.value = false
    }
    const loadVideo = url => {
      if (Hls.isSupported()) {
        const video = document.getElementById('video')
        if (url.endsWith('.mp4')) {
          video.src = url
          video.play()
        } else {
          const hls = new Hls()
          hls.loadSource(url)
          hls.attachMedia(video)
          hls.on(Hls.Events.MANIFEST_PARSED, () => {
            video.play()
          })
        }
      }
    }
    const openVideoUpload = () => {
      context.refs.openUpload.click()
    }
    const searchCreative = () => {
      creativePage.value = 0
      creatives.value = []
      loadCreatives()
    }
    const loadingGallery = ref(true)
    const loadCreatives = async ($state, load) => {
      if (load) {
        loadingGallery.value = load
      }
      const filterModel = {
        page: creativePage.value,
        type: IMAGE_FORMAT_ID,
        searchTerm: creativeSearchTerm.value,
        formatId: IMAGE_FORMAT_ID,
      }
      try {
        const response = await GalleryService.getCreatives(filterModel, context.root.$route.params.accountId)
        if (response.length) {
          creativePage.value += 1
          creatives.value.push(...response)
          if ($state) { $state.loaded() }
        } else if ($state) { $state.complete() }
      } catch (error) {
        notification.methods.error(error.message)
      }
      loadingGallery.value = false
    }
    const selectCreative = creative => {
      if (creatives.value.some(e => e.selected === true)) {
        (creatives.value.find(x => x.selected === true)).selected = false
      }
      creative.selected = !creative.selected
    }
    const dropzoneChangeUrl = type => {
      const clientApi = getAPIDomain()
      if (type === 'Square') {
        context.refs.companionDropzone.setOption('url', `${clientApi}/upload/ad?subtype=${ADSUBTYPE_DISPLAY_IMAGE}&accountId=${accountId}`)
      } else {
        context.refs.companionUnderVideoDropzone.setOption('url', `${clientApi}/upload/ad?accountId=${accountId}`)
      }
    }
    const sendFile = (file, xhr, formData) => {
      formData.append('file', file)
    }
    const showGallery = (event, type) => {
      isSquare.value = type === 'Square'
      event.stopPropagation()
      loadCreatives(null, true)
      context.refs.galleryModal.show()
    }
    const hideGallery = () => {
      creativeSearchTerm.value = ''
      context.refs.galleryModal.hide()
    }
    const checkFileThumbnail = file => {
      savingImage.value = true
      // eslint-disable-next-line no-undef
      file.acceptDimensions = () => done
    }
    const uploadFile = (file, response) => {
      let element = null
      if (response.results.length > 0) {
        // eslint-disable-next-line prefer-destructuring
        element = response.results[0]
      }
      if (element && element.inserted) {
        model.value.companionAdModel.displayAd.fileName = element.fileName
        model.value.companionAdModel.displayAd.mediaUrl = element.imageLink
        model.value.companionAdModel.displayAd.formatId = element.formatId
        model.value.companionAdModel.displayAd.width = file.width
        model.value.companionAdModel.displayAd.height = file.height
        model.value.companionAdModel.displayAd.show = true
      }
      if (!response.success) uploadMessage.value = response
      savingImage.value = false
    }
    const uploadUnderVideoAdFile = (file, response) => {
      let element = null
      if (response.results.length > 0) {
        // eslint-disable-next-line prefer-destructuring
        element = response.results[0]
      }
      if (element && element.inserted) {
        model.value.companionAdModel.nativeAd.customProperties.find(x => x.id === 6).value = element.imageLink
        model.value.companionAdModel.nativeAd.width = file.width
        model.value.companionAdModel.nativeAd.height = file.height
        model.value.companionAdModel.nativeAd.formatId = NATIVE_FORMAT_ID
        showImage.value = true
      }
      if (!response.success) uploadUnderVideoMessage.value = response
      savingImage.value = false
    }
    const removeFile = file => {
      const { type } = model.value.companionAdModel
      if (type === 1 || type === 2) {
        context.refs.companionDropzone.removeFile(file)
      }
      if (type === 2 || type === 3) {
        context.refs.companionUnderVideoDropzone.removeFile(file)
      }
    }
    const removeImage = type => {
      if (type === 'Square') {
        uploadMessage.value = ''
        model.value.companionAdModel.displayAd.fileName = ''
        model.value.companionAdModel.displayAd.mediaUrl = ''
        model.value.companionAdModel.displayAd.formatId = ''
        model.value.companionAdModel.displayAd.show = false
      } else {
        uploadUnderVideoMessage.value = ''
        model.value.companionAdModel.nativeAd.customProperties.find(x => x.id === 6).value = ''
        model.value.companionAdModel.nativeAd.width = ''
        model.value.companionAdModel.nativeAd.height = ''
        model.value.companionAdModel.nativeAd.formatId = ''
        showImage.value = false
      }
    }
    const addCreativeItems = () => {
      const selectedCreative = creatives.value.find(x => x.selected === true)
      model.value.companionAdModel.displayAd.fileName = selectedCreative.name
      model.value.companionAdModel.displayAd.mediaUrl = selectedCreative.url
      model.value.companionAdModel.displayAd.formatId = IMAGE_FORMAT_ID
      model.value.companionAdModel.displayAd.show = true
      model.value.companionAdModel.displayAd.adName = model.value.companionAdModel.name
      selectedCreative.selected = false
      hideGallery()
    }
    const previewAd = ref('')
    const showImagePreview = async () => {
      try {
        const response = await AdsService.sanitazeUrl(model.value.companionAdModel.nativeAd.url)
        model.value.companionAdModel.nativeAd.url = response
      } catch (error) {
        notification.methods.error(error.message)
      }

      previewAd.value = model.value.companionAdModel.nativeAd.htmlContent
      // eslint-disable-next-line no-plusplus
      for (let i = 0; i < model.value.companionAdModel.nativeAd.customProperties.length; i++) {
        const prop = model.value.companionAdModel.nativeAd.customProperties[i]
        let propValue = prop.value
        if (propValue == null || !propValue.length) propValue = prop.displayName
        previewAd.value = previewAd.value.split(`{{${prop.name}}}`).join(propValue)
      }
      if (context.refs.previewFrameDesktop) {
        context.refs.previewFrameDesktop.srcdoc = previewAd.value
        context.refs.previewFrameMobile.srcdoc = `<div class="mobile">${previewAd.value}</div>`
      }
    }
    return {
      model,
      loading,
      configuration,
      accountId,
      // Data
      videoEdit,
      selectedVideo,
      savingImage,
      preview,
      previewVideo,
      errors,
      customizeColors,
      value,
      platformAccessManager,
      isUploading,
      fileProgress,
      barVariant,
      isInEditMode,
      dropzoneOptions,
      creatives,
      uploadMessage,
      uploadUnderVideoMessage,
      creativeSearchTerm,
      showImage,
      uploaderBarMessage,
      dropzoneOptionsVideo,
      loadingGallery,
      encoding,
      // Functions
      externalSwitched,
      isFormCompleted,
      removeImage,
      setSaving,
      saveCompanionAd,
      setSelectedVideo,
      uploadVideo,
      updateVideoName,
      showPreview,
      onFileSelected,
      loadCreatives,
      loadVideo,
      openVideoUpload,
      searchCreative,
      addCreativeItems,
      removeFile,
      uploadUnderVideoAdFile,
      uploadFile,
      checkFileThumbnail,
      showGallery,
      sendFile,
      dropzoneChangeUrl,
      hideGallery,
      showImagePreview,
      selectCreative,
      checkFileType,
      passFile,
      clearErrorMsg,
    }
  },
}
</script>
<style lang="scss" >
@import '@/assets/scss/views/client/ads-manager/create/ads/companion-ad.scss';
@import '@/assets/scss/libs/_vue-multiselect.scss';
.gallery_loader{
  min-height: 490px !important;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
